import { useContext } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ApiCacheContext } from '@services/ApiCacheContext';
import { SERVICE_TYPE, MODAL_MODE } from '@services/ModalParameterContext';
import { onBeforeUnmount, Ref, computed, reactive, ref } from '@vue/composition-api';
import { npAtobaraiTheme, ThemeContext } from '@berlin-front/common/services/ThemeContext';
import useHelp from '@berlin-front/common/composables/useHelp';
import { event } from 'vue-gtag';
import { camelCaseDeep, notNil, includes, formatNumber, zodErrorToMessage } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import { broadcast } from '@/broadcast';
import { npAtobaraiSettlementServiceParameterValidator } from '@schemas/np-atobarai-settlement/NpAtobaraiSettlementParameterSchema';
import { CamelCasedPropertiesDeep } from 'type-fest';
import useClientConnection from '@berlin-front/common/composables/useClientConnection';
import { npAtobaraiSettlementCallbackDefinition } from '@schemas/np-atobarai-settlement/NpAtobaraiSettlementCallbackSchema';
import { useErrorHandle } from '@composables/useErrorHandle';
import { builtinCallbackDefinition } from '@schemas/common-schemas/builtin-callback-schema';
import useSpacer from '@composables/useSpacer';
import commonParamAdditionalSpace from '@schemas/common-schemas/common-param-additional-space';
import type { ServiceDiscriminator, ServiceGlobals } from './index';

/**
 * このファイルでハンドリングするサービスタイプ。
 */
const CURRENT_SERVICE_TYPE = SERVICE_TYPE.ATOBARAI;

/**
 * このファイルでハンドルするモーダルモード（決済モーダル）
 */
const CURRENT_MODE = MODAL_MODE.SETTLEMENT;

/**
 * このファイルでハンドルするエラー画面のルート名。
 */
const CURRENT_ERROR_ROUTE_NAME = ROUTE_NAME.ERROR_VIEW;

/**
 * バリデータ
 */
const VALIDATOR = npAtobaraiSettlementServiceParameterValidator;

/**
 * 現在実行しているサービスを判定する。
 *
 * メッセージイベントを処理するかどうか、現在のサービスタイプを判定して決定する。
 * 処理成功、失敗、キャンセルなどの結果処理は汎用イベントメッセージを使用しているため、全てのブリッジに届いてしまう。
 * そのため、現在のサービスタイプとブリッジの担当するサービスタイプが一致する時のみメッセージを処理するように実装する。
 * @param serviceType
 * @returns
 */
const isCurrentService = (param: unknown): boolean =>
  VALIDATOR.pick({ service_type: true, modal_mode: true }).safeParse(param).success;

/**
 *
 * @param currentService
 * @returns
 */
const useBridge = (currentService: Ref<ServiceDiscriminator>) => {
  /**
   * 初期化済みフラグ。
   *
   * init呼び出しに成功したらtrueとなり、失敗したらfalseとなる。
   */
  const initializedRef = ref(false);

  /**
   * 未整形の状態のpayment
   *
   * pre_paymentチェックと、JSErrorApiにぶん投げるのに使う。
   */
  const rawPaymentRef = ref<(typeof VALIDATOR._input)['payment'] | undefined>(undefined);

  /**
   * バリデータを通過し、キャメルに変換されたパラメータ。
   */
  let normalizedParameter: CamelCasedPropertiesDeep<typeof VALIDATOR._output> | undefined;

  /**
   * 店舗公開鍵
   */
  const publicKeyRef = ref<string | undefined>(undefined);

  const router = useRouter();

  /**
   * コールバック実行基盤
   */
  const connection = useClientConnection();

  const { registTransactionPostResult, npAtobaraiModalParameter, setNpAtobaraiModalParameter } =
    useContext(ApiCacheContext);
  const { setThemeParameter } = useContext(ThemeContext);

  // サービスタイプ別のグローバルオブジェクト。
  const headerTotalPriceUnit = computed(() => '円');
  const headerTotalPrice = computed(() => {
    if (npAtobaraiModalParameter.value) {
      return formatNumber(npAtobaraiModalParameter.value.payment.amount);
    }
    return '';
  });
  const pubkey = computed(() => (initializedRef.value ? npAtobaraiModalParameter.value.pubKey : ''));
  const help = useHelp();
  const serviceGlobals: Ref<ServiceGlobals | undefined> = computed(() =>
    initializedRef.value
      ? {
          ...reactive({
            requestId: undefined,
            serviceType: CURRENT_SERVICE_TYPE,
            errorRouteName: CURRENT_ERROR_ROUTE_NAME,
            pubkey,
            headerShopName: '',
            headerTotalPrice,
            headerTotalPriceUnit,
            // 登録フォームなどないため、初期値もない。
            registerFormDefault: undefined,
          }),
          ...help,
        }
      : undefined,
  );

  const isActive = computed(() => isCurrentService(currentService.value));

  // 余白コントロール
  const additionalSpaceRef = ref({ top: 0, bottom: 0 });
  useSpacer({
    isActiveRef: isActive,
    additionalSpaceRef,
  });

  // エラーハンドル処理。
  // APIエラーとJSエラーをハンドルして、コールバックとJSErrorAPIの実行をやってくれる。
  useErrorHandle({
    activeRef: isActive,
    // TODO: atone決済専用のERROR_VIEWを用意する。
    errorRouteName: CURRENT_ERROR_ROUTE_NAME,
    activeService: {
      service_type: CURRENT_SERVICE_TYPE,
      modal_mode: CURRENT_MODE,
    },
    paymentRef: rawPaymentRef,
    publicKeyRef,
    errorCallback: (param) => {
      void connection.executeCallback(npAtobaraiSettlementCallbackDefinition, 'error', [
        {
          service_type: '02',
          modal_mode: '01',
          ...param,
        },
      ]);
    },
  });
  // エラー以外のイベント処理。
  const unlistenList = [
    broadcast.listen('MODAL_CONTROL_SUCCEEDED', () => {
      if (!isCurrentService(currentService.value)) return;

      const result = notNil(registTransactionPostResult.value);
      void connection.executeCallback(npAtobaraiSettlementCallbackDefinition, 'succeeded', [
        {
          service_type: '02',
          modal_mode: '01',
          authorization_result: result.authorizationResult,
          shop_code: result.shopCode,
          shop_transaction_no: result.shopTransactionId,
          transaction_id: result.npTransactionId,
        },
      ]);

      void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
        void router.replace({ name: ROUTE_NAME.HOME });
      });
    }),
    broadcast.listen('MODAL_CONTROL_FAILED', (payload) => {
      if (!isCurrentService(currentService.value)) return;
      if (payload.noResult) {
        void connection.executeCallback(npAtobaraiSettlementCallbackDefinition, 'failed', [
          {
            service_type: '02',
            modal_mode: '01',
          },
        ]);
      } else {
        const result = notNil(registTransactionPostResult.value);
        void connection.executeCallback(npAtobaraiSettlementCallbackDefinition, 'failed', [
          {
            service_type: '02',
            modal_mode: '01',
            authorization_result: result.authorizationResult,
            authorization_result_ng_reason: notNil(result.authorizationNgReason),
            shop_code: result.shopCode,
            shop_transaction_no: npAtobaraiModalParameter.value.payment.shopTransactionNo,
            transaction_id: result.npTransactionId,
          },
        ]);
      }

      void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
        void router.replace({ name: ROUTE_NAME.HOME });
      });
    }),
    broadcast.listen('MODAL_CONTROL_CANCELLED', () => {
      if (!isCurrentService(currentService.value)) return;

      void connection.executeCallback(npAtobaraiSettlementCallbackDefinition, 'cancelled', [
        {
          service_type: '02',
          modal_mode: '01',
        },
      ]);
      window.newrelic.addPageAction('close_modal');

      void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
        void router.replace({ name: ROUTE_NAME.HOME });
      });
    }),
    router.afterEach((to) => {
      if (!isCurrentService(currentService.value)) return;
      // モーダル表示を行うタイミングは、ホームかローディング以外の画面に到達した時。
      // エラー画面ということで良い。
      if (!includes(...[ROUTE_NAME.HOME, ROUTE_NAME.NP_ATOBARAI_LOADING])(to.name)) {
        void connection.executeCallback(builtinCallbackDefinition, 'show', []);
      }
    }),
  ] as const;
  onBeforeUnmount(() => unlistenList.forEach((unlisten) => unlisten()));

  /**
   * service_typeとmodal_modeをチェックする。
   * @param rawParameter
   * @returns
   */
  const check: (rawParameter: unknown) => boolean = isCurrentService;

  /**
   * 初期化リクエストメッセージハンドラ
   *
   * ショップサイトからのメッセージをハンドリングする3つのハンドラの一つ。
   *
   * 開始前の初期データ設定。
   *
   * - 初期化処理として事前に呼び出し可能なAPIなどを実行しておき、モーダル起動処理時間を短縮する。
   * - 初期化が終わったらREADYメッセージを応答すること。
   *
   * ショップサイト側のモジュールはREADYイベントが発生するまでは他のメッセージを保留（キューに溜め置き待機）する。
   *
   * @param rawParameter
   * @returns
   */
  const init: (rawParameter: unknown) => void = (rawParameter) => {
    // 生の決済パラメータはlog用で、まっさきに保存する。
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rawPaymentRef.value = (rawParameter as any)?.payment ?? {};

    // 余白指定を取り込む
    const additionalSpaceParseResult = commonParamAdditionalSpace.safeParse(rawParameter);
    if (additionalSpaceParseResult.success) {
      additionalSpaceRef.value = additionalSpaceParseResult.data.additional_space;
    }

    const parseResult = npAtobaraiSettlementServiceParameterValidator.safeParse(rawParameter);
    if (!parseResult.success) {
      initializedRef.value = false;
      const { name, stack } = parseResult.error;
      const message = zodErrorToMessage(parseResult.error);
      // エラーを通知
      broadcast.send({
        type: 'JS_ERROR',
        payload: {
          service: {
            service_type: CURRENT_SERVICE_TYPE,
            modal_mode: CURRENT_MODE,
          },
          name,
          message,
          stack,
        },
      });
      return;
    }
    const parameter = parseResult.data;
    publicKeyRef.value = parameter.pub_key;
    normalizedParameter = camelCaseDeep(parameter);
    // パラメタをキャッシュする。
    setNpAtobaraiModalParameter(normalizedParameter);

    // GA4イベント送信
    event('service_type', { service_type: 'NP Atobarai' });

    initializedRef.value = true;
  };

  /**
   * 開始リクエストメッセージハンドラ
   *
   * ショップサイトからのメッセージをハンドリングする3つのハンドラの一つ。
   * 決済モーダルの起動メッセージを処理する。
   * @param rawParameter
   * @returns
   */
  const start: (rawParameter: unknown) => void = (rawParameter) => {
    if (!isCurrentService(rawParameter)) return;

    // モーダル開く時に現在のサービスタイプを設定する。ここでisActiveがtrueになるため最優先で設定する。
    currentService.value = { service_type: CURRENT_SERVICE_TYPE, modal_mode: CURRENT_MODE };
    // モーダルを開くタイミングでUIテーマを設定する。
    setThemeParameter(npAtobaraiTheme());

    if (!initializedRef.value) {
      broadcast.send({
        type: 'JS_ERROR',
        payload: {
          service: {
            service_type: CURRENT_SERVICE_TYPE,
            modal_mode: CURRENT_MODE,
          },
          name: 'RequestException',
          message: '未初期化、もしくは不正なパラメータが与えられた状態で処理が開始されました。',
        },
      });
      void router.replace({ name: CURRENT_ERROR_ROUTE_NAME, params: { errorType: 'shop' } });
      return;
    }

    // ローディング画面へ遷移。
    void router.replace({ name: ROUTE_NAME.NP_ATOBARAI_LOADING });
    // 表示を指示しない。後払いはローディングを見せないため。
    // sendMessageToParent({ type: 'TRANSITION_IN' });
  };

  // ハンドラーを返却する。
  return {
    init,
    start,
    check,
    serviceGlobals,
    serviceDiscriminator: { service_type: CURRENT_SERVICE_TYPE, modal_mode: CURRENT_MODE },
  };
};

export default useBridge;
